// COPIED from Remote-Leverage/hriq@3b50a234afdd packages/design-system/components/ui/v2/card.tsx by apps/web/scripts/sync-design-system.mjs. Do not edit: change it in Hriq and re-sync.
// @ts-nocheck -- type-checked in Hriq under its own compiler options (the CRM's are stricter).
import { Card as CardV1 } from "@repo/design-system/components/ui/card";
import { cn } from "@repo/design-system/lib/utils";
import type { ComponentProps } from "react";

/**
 * v2 Card — restyles v1's `Card` root (`components/ui/card.tsx`) to match
 * the Figma "Remote Leverage — Design System" file, "Onboarding Card"
 * component node `82:321` — the only card container reference in the
 * file; Card is the shared container Selection Card, Summary Strip, and
 * Task all build on. Only the container's box treatment is overridden
 * here (background, radius, padding, hover/focus/disabled) — the same
 * DOM structure, `data-slot`s, and sub-parts (`CardHeader`, `CardTitle`,
 * etc.) are v1's own, re-exported unstyled below.
 *
 * Resting background is v1's own `bg-card` (resolves to white in light
 * mode already), not overridden.
 *
 * Deliberately out of scope (no generalizable design exists):
 * - Figma's "Finished" state (translucent bg, white border, fixed 82px
 *   height) is specific to the Onboarding Card composite's content, not
 *   a generic container treatment — left out per this ticket's scope
 *   decision. A future composite can layer it on top of this file.
 * - Sub-part restyling (`CardHeader`/`CardContent`/etc. padding) is out
 *   of scope for this ticket, which restyles only the Card container.
 *
 * `disabled` is a plain boolean prop (not a native attribute — Card
 * renders a `<div>`) that sets `aria-disabled` and applies the Figma
 * disabled treatment (50% opacity, no pointer events). Hover uses a
 * native `hover:` pseudo-class since it's a browser-driven state; focus
 * uses `focus-visible:`/`focus-within:` so a consumer that makes the
 * card focusable (e.g. `tabIndex`, or nesting a focusable control) gets
 * Figma's ring for free, without Card needing its own `focus` prop.
 */

type CardProps = ComponentProps<typeof CardV1> & {
  disabled?: boolean;
};

function Card({ className, disabled = false, ...props }: CardProps) {
  return (
    <CardV1
      aria-disabled={disabled || undefined}
      className={cn(
        "ds-v2 gap-4 rounded-[16px] border-transparent px-5 py-4 shadow-none",
        "hover:bg-(--v2-card-bg-hover)",
        "focus-within:shadow-[0_0_0_3px_var(--v2-card-focus-ring)] focus-visible:shadow-[0_0_0_3px_var(--v2-card-focus-ring)] focus-visible:outline-none",
        disabled ? "pointer-events-none opacity-50" : null,
        className
      )}
      {...props}
    />
  );
}

// biome-ignore lint/performance/noBarrelFile: re-exporting v1's unstyled Card sub-parts verbatim, not a barrel of unrelated modules
export {
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/design-system/components/ui/card";
export { Card };
export type { CardProps };
